// WebAuthn passkey + PRF extension — tier-1 unlock for the NC1 identity.
//
// The PRF output (32 bytes minted inside the authenticator, deterministic per
// credential + salt) feeds shared/kek.js wrapToWebauthn. The salt is FIXED
// PER ACCOUNT so unlock can evaluate the PRF before knowing which credential
// answers; it is recorded (base64) in every wraps.webauthn entry.
//
// Support is fragmented (chunk-0 probe matrix): current Chrome, Safari 18+,
// Firefox uneven, Android WebView likely absent. Every caller must treat
// failure as "fall back to recovery code / device-transfer", never as fatal.

function toB64(buf) {
  const bytes = new Uint8Array(buf)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}

function fromB64(b64) {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes
}

function randomBytes(n) {
  const bytes = new Uint8Array(n)
  crypto.getRandomValues(bytes)
  return bytes
}

/** Cheap capability sniff — a real answer needs the create() below. */
export async function prfProbablySupported() {
  try {
    if (typeof window === 'undefined' || !window.PublicKeyCredential || !navigator.credentials) {
      return false
    }
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

/**
 * Create a passkey requesting the PRF extension, then evaluate the PRF for
 * `prfSaltB64`. Some authenticators evaluate at create(); the rest need a
 * follow-up get() (one extra biometric touch — so signup shows TWO prompts
 * on those platforms, ONE where eval-at-create works). Decision 2026-08-12:
 * keep the two-touch flow — deferring the get() risks a passkey whose wrap
 * is never published, because the signup browser's warm keystore cache means
 * a get() may never naturally recur. Returns
 * { credentialIdB64, prfOutput: Uint8Array } or throws — callers degrade to
 * recovery-code-only.
 */
export async function registerPasskeyWithPrf({ email, prfSaltB64 }) {
  const salt = fromB64(prfSaltB64)

  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32),
      rp: { name: process.env.VUE_APP_NAME || window.location.hostname },
      user: {
        id: randomBytes(16),
        name: email,
        displayName: email,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },   // ES256
        { type: 'public-key', alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        residentKey: 'preferred',
        userVerification: 'required',
      },
      extensions: { prf: { eval: { first: salt } } },
    },
  })
  if (!credential) throw new Error('passkey creation returned no credential')

  const ext = credential.getClientExtensionResults()
  if (!ext.prf || ext.prf.enabled === false) {
    throw new Error("This passkey can't be used for encryption on this device.")
  }

  const credentialIdB64 = toB64(credential.rawId)

  if (ext.prf.results && ext.prf.results.first) {
    return { credentialIdB64, prfOutput: new Uint8Array(ext.prf.results.first) }
  }

  // PRF supported but not evaluated at create() — evaluate via an assertion.
  const { prfOutput } = await evaluatePrf({ credentialIdsB64: [credentialIdB64], prfSaltB64 })
  return { credentialIdB64, prfOutput }
}

/**
 * Evaluate the PRF for an existing credential (silent steady-state unlock).
 * Returns { credentialIdB64, prfOutput: Uint8Array } for whichever listed
 * credential answered.
 */
export async function evaluatePrf({ credentialIdsB64, prfSaltB64 }) {
  const salt = fromB64(prfSaltB64)

  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32),
      allowCredentials: (credentialIdsB64 || []).map((id) => ({
        type: 'public-key',
        id: fromB64(id),
      })),
      userVerification: 'required',
      extensions: { prf: { eval: { first: salt } } },
    },
  })
  if (!assertion) throw new Error('passkey assertion returned no credential')

  const ext = assertion.getClientExtensionResults()
  const first = ext.prf && ext.prf.results && ext.prf.results.first
  if (!first) {
    // Seen 2026-10-09: a passkey saved by Edge, used from Chrome on the same
    // Windows machine, answers the assertion but returns no PRF output.
    const err = new Error("This passkey can't unlock encryption on this device.")
    err.code = 'prf-missing'
    throw err
  }

  return { credentialIdB64: toB64(assertion.rawId), prfOutput: new Uint8Array(first) }
}

export function generatePrfSaltB64() {
  return toB64(randomBytes(32))
}
